import React, { useState, useEffect } from "react";

export default function TextTools({
	canvasRef,
	layers,
	drawCanvas,
	updateAnySelected,
	selectedTextIsWhite,
	setSelectedTextIsWhite,
	selectedFontFamily,
	setSelectedFontFamily,
	isBold,
	setIsBold,
	isItalic,
	setIsItalic,
	isUnderlined,
	setIsUnderlined,
	isStrikethrough,
	setIsStrikethrough,
	isAllCaps,
	setIsAllCaps,
	textOpacity,
	setTextOpacity,
	clampTextInsideBgRect,
	topTextInput,
	setTopTextInput,
	bottomTextInput,
	setBottomTextInput,
	extraTextInputs,
	setExtraTextInputs,
	hasBackground,
	bgRect,
	forceUpdateLayers,
}) {
	const isMobile =
		typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

	/**
	 * Button click => Add a new empty string to extraTextInputs
	 */
	const handleAddExtraText = () => {
		setExtraTextInputs((prev) => [...prev, ""]);
	};

	const handleDeleteSelectedText = () => {
		// We'll store which single text layer is selected
		let singleSelectedLayer = null;
		const selectedIndices = [];

		layers.current.forEach((layer, index) => {
			if (layer.type === "text" && layer.isSelected) {
				selectedIndices.push(index);
				singleSelectedLayer = layer; // we assume only one item at a time
			}
		});

		// Remove them from layers (back to front so indices don't shift)
		for (let i = selectedIndices.length - 1; i >= 0; i--) {
			layers.current.splice(selectedIndices[i], 1);
		}

		// If we found a selected text layer, clear its matching input
		if (singleSelectedLayer) {
			if (typeof singleSelectedLayer.extraIndex === "number") {
				// It's one of the extra text layers
				setExtraTextInputs((prev) => {
					return prev.filter((_, i) => i !== singleSelectedLayer.extraIndex);
				});
			} else {
				// It's either top or bottom text
				if (singleSelectedLayer.isTop) {
					setTopTextInput("");
				} else {
					setBottomTextInput("");
				}
			}
		}

		// Finally update, re-draw, etc.
		updateAnySelected?.();
		drawCanvas();
		forceUpdateLayers();
	};

	// ─────────────────────────────────────────────────────────────────────────────
	//                 EXISTING: TOP/BOTTOM TEXT HANDLING
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Finds or creates a text layer for top/bottom in layers.current
	 */
	const ensureTextLayer = (isTop) => {
		// Attempt to find an existing top/bottom text layer
		let layer = layers.current.find(
			(l) => l.type === "text" && l.isTop === isTop && l.extraIndex == null
		);
		if (!layer) {
			const canvas = canvasRef.current;
			if (!canvas) return null;

			const fontSize = 50;
			const color = selectedTextIsWhite ? "#ffffff" : "#000000";

			let x, y;

			// If we have a valid background rect with non-zero width/height,
			// place the text relative to that. Otherwise, fallback.
			if (bgRect?.current && bgRect.current.width > 0 && bgRect.current.height > 0) {
				const { x: bgX, y: bgY, width: bgW, height: bgH } = bgRect.current;

				x = bgX + bgW / 2; // center horizontally on the background
				if (isTop) {
					y = bgY + 50; // top edge + 50
				} else {
					y = bgY + bgH - 50; // bottom edge - 50
				}
			} else {
				// fallback to entire canvas
				x = canvas.width / 2;
				y = isTop ? 50 : canvas.height - 50;
			}

			// Deselect everything else
			layers.current.forEach((l) => (l.isSelected = false));

			// Create the new layer
			layer = {
				type: "text",
				text: "",
				x,
				y,
				fontSize,
				color,
				rotation: 0,
				isDragging: false,
				isRotating: false,
				isResizing: false,
				isSelected: true,
				dragOffsetX: 0,
				dragOffsetY: 0,
				rotationStart: 0,
				startAngle: 0,
				fontFamily: selectedFontFamily,
				isBold,
				isItalic,
				isUnderlined,
				isStrikethrough,
				isAllCaps,
				opacity: textOpacity,
				isTop: isTop, // mark it as top or bottom
				storedLines: null, // we will store the final array of lines here
				recalculateWrap: true,
			};

			layers.current.push(layer);
			updateAnySelected?.();
		}

		return layer;
	};

	/**
	 * If user empties the top/bottom input, remove the layer from the array
	 */
	const removeTextLayer = (isTop) => {
		const idx = layers.current.findIndex(
			(l) => l.type === "text" && l.isTop === isTop && l.extraIndex == null
		);
		if (idx !== -1) {
			layers.current.splice(idx, 1);
			updateAnySelected?.();
		}
	};

	/**
	 * Whenever topTextInput changes, create/update the top text layer
	 */
	useEffect(() => {
		if (!canvasRef.current) return;

		if (topTextInput.replace(/\s/g, "") === "") {
			// Remove if empty
			removeTextLayer(true);
			drawCanvas();
		} else {
			const topLayer = ensureTextLayer(true);
			if (topLayer) {
				topLayer.text = topTextInput;
				topLayer.recalculateWrap = true;

				const canvas = canvasRef.current;
				if (canvas) {
					const ctx = canvas.getContext("2d");
					clampTextInsideBgRect(topLayer, ctx, bgRect.current);
				}
				drawCanvas();
			}
		}
	}, [topTextInput, canvasRef, drawCanvas]);

	/**
	 * Whenever bottomTextInput changes, create/update the bottom text layer
	 */
	useEffect(() => {
		if (!canvasRef.current) return;

		if (bottomTextInput.replace(/\s/g, "") === "") {
			removeTextLayer(false);
			drawCanvas();
		} else {
			const bottomLayer = ensureTextLayer(false);
			if (bottomLayer) {
				bottomLayer.text = bottomTextInput;
				bottomLayer.recalculateWrap = true;

				const canvas = canvasRef.current;
				if (canvas) {
					const ctx = canvas.getContext("2d");
					clampTextInsideBgRect(bottomLayer, ctx, bgRect.current);
				}
				drawCanvas();
			}
		}
	}, [bottomTextInput, canvasRef, drawCanvas]);

	// ─────────────────────────────────────────────────────────────────────────────
	//                NEW: EXTRA TEXT INPUTS HANDLING
	// ─────────────────────────────────────────────────────────────────────────────

	/**
	 * Ensures a text layer for a *specific extra index*.
	 * We will give each layer a property `extraIndex = i` to keep track.
	 */
	const ensureExtraTextLayer = (index) => {
		// Attempt to find an existing layer with this extraIndex
		let layer = layers.current.find((l) => l.type === "text" && l.extraIndex === index);

		if (!layer) {
			const canvas = canvasRef.current;
			if (!canvas) return null;

			const fontSize = 50;
			const color = selectedTextIsWhite ? "#ffffff" : "#000000";

			const x = canvas.width / 2;
			const y = canvas.height / 2;
			layers.current.forEach((l) => (l.isSelected = false));
			layer = {
				type: "text",
				text: "",
				x,
				y,
				fontSize,
				color,
				rotation: 0,
				isDragging: false,
				isRotating: false,
				isResizing: false,
				isSelected: false,
				dragOffsetX: 0,
				dragOffsetY: 0,
				rotationStart: 0,
				startAngle: 0,
				fontFamily: selectedFontFamily,
				isBold,
				isItalic,
				isUnderlined,
				isStrikethrough,
				isAllCaps,
				opacity: textOpacity,
				extraIndex: index,
				isSelected: true,
			};

			layers.current.push(layer);
			updateAnySelected?.();
		}

		return layer;
	};

	/**
	 * Removes a text layer for a given extraIndex
	 */
	const removeExtraTextLayer = (index) => {
		const idx = layers.current.findIndex((l) => l.type === "text" && l.extraIndex === index);
		if (idx !== -1) {
			layers.current.splice(idx, 1);
			updateAnySelected?.();
		}
	};

	/**
	 * Whenever the user changes *any* of the extra text inputs, update the layers.
	 */
	useEffect(() => {
		if (!canvasRef.current) return;
		let shouldRedraw = false;

		extraTextInputs.forEach((textValue, i) => {
			if (textValue.trim() === "") {
				// Remove if empty
				removeExtraTextLayer(i);
				shouldRedraw = true;
			} else {
				const layer = ensureExtraTextLayer(i);
				if (layer) {
					layer.text = textValue;
					layer.recalculateWrap = true;
					shouldRedraw = true;
				}
			}
		});

		if (shouldRedraw) {
			drawCanvas();
		}
	}, [extraTextInputs, canvasRef, drawCanvas]);

	// ─────────────────────────────────────────────────────────────────────────────
	//                         COLOR TOGGLE
	// ─────────────────────────────────────────────────────────────────────────────

	const handleColorToggleChange = (isWhite) => {
		setSelectedTextIsWhite(isWhite);

		// Update the color of whichever text item(s) are selected
		const selectedItems = layers.current.filter((t) => t.isSelected && t.type === "text");
		selectedItems.forEach((item) => {
			item.color = isWhite ? "#ffffff" : "#000000";
		});
		drawCanvas();
	};

	// ─────────────────────────────────────────────────────────────────────────────
	//                               RENDER
	// ─────────────────────────────────────────────────────────────────────────────

	return (
		<div className="flex flex-col items-start md:gap-8">
			{/* GROUP 1: Top/Bottom Text + Add Text Button */}
			<div className="flex flex-col gap-2">
				<div className="flex flex-wrap items-start gap-2 rounded-md">
					<textarea
						placeholder="Top Text"
						value={topTextInput}
						rows={1}
						onChange={(e) => setTopTextInput(e.target.value)}
						className="px-2 py-1 w-36 md:w-44 rounded-md bg-monadDark text-white border text-sm md:text-base border-monadGray disabled:cursor-not-allowed disabled:opacity-60"
						disabled={!hasBackground}
					/>
					<textarea
						placeholder="Bottom Text"
						value={bottomTextInput}
						rows={1}
						onChange={(e) => setBottomTextInput(e.target.value)}
						className="px-2 py-1 w-36 md:w-44 rounded-md bg-monadDark text-white border text-sm md:text-base border-monadGray  disabled:cursor-not-allowed disabled:opacity-60"
						disabled={!hasBackground}
					/>
				</div>
				<div className="flex items-center gap-2">
					<button
						onClick={handleAddExtraText}
						disabled={!hasBackground}
						className="
			  px-2 py-1 w-36 md:w-44 bg-monadAccent text-white text-sm md:text-base rounded self-start
			  disabled:cursor-not-allowed disabled:opacity-60
			"
					>
						Add New Text
					</button>
					<button
						onClick={handleDeleteSelectedText}
						disabled={!hasBackground}
						className="
              px-2 py-1 w-36 md:w-44 bg-red-600 text-white text-sm md:text-base rounded self-start
              disabled:cursor-not-allowed disabled:opacity-60
            "
					>
						Delete Selected
					</button>
				</div>
				{/* EXTRA TEXT FIELDS */}
				<div className="flex flex-col gap-1 mt-1">
					{extraTextInputs.map((textVal, index) => (
						<textarea
							key={index}
							rows={1}
							placeholder={`Extra Text #${index + 1}`}
							value={textVal}
							onChange={(e) => {
								const newVal = e.target.value;
								setExtraTextInputs((prev) => {
									const copy = [...prev];
									copy[index] = newVal;
									return copy;
								});
							}}
							className="
				  px-2 py-1 w-[150px] rounded-md bg-monadDark text-white border text-sm md:text-base border-monadGray 
				  disabled:cursor-not-allowed disabled:opacity-60
				"
							disabled={!hasBackground}
						/>
					))}
				</div>
			</div>

			{/* GROUP 2: Remaining Font & Style Controls */}
			<div className="flex flex-col gap-4 md:gap-6 items-start mt-4">
				<div className="flex items-start gap-2 text-sm md:text-base">
					<span className="text-monadWhite">Font:</span>
					<select
						value={selectedFontFamily}
						onChange={(e) => {
							const newFont = e.target.value;

							// 1) Update your global `selectedFontFamily` state
							setSelectedFontFamily(newFont);

							// 2) Update the *actual layer objects* that are selected
							layers.current.forEach((layer) => {
								if (layer.type === "text" && layer.isSelected) {
									layer.fontFamily = newFont;
								}
							});

							// 3) (Optional) Wait for the font to load (in case it’s not loaded yet)
							document.fonts.load(`12px "${newFont}"`).then(() => {
								drawCanvas();
							});
						}}
						className="px-2 py-1 rounded bg-monadDark text-white text-sm md:text-base border border-monadGray"
					>
						<option style={{ fontFamily: "MyImpact" }}>Impact</option>
						<option style={{ fontFamily: "MyArial" }}>Arial</option>
						{!isMobile && <option style={{ fontFamily: "MyComicSansMS" }}>Comic Sans MS</option>}
						<option style={{ fontFamily: "MyMontserrat" }}>Montserrat</option>
						<option style={{ fontFamily: "MyVerdana" }}>Verdana</option>
						<option style={{ fontFamily: "MyCourierNew" }}>Courier New</option>
						<option style={{ fontFamily: "MyHelvetica" }}>Helvetica</option>
						<option style={{ fontFamily: "MyTimesNewRoman" }}>Times New Roman</option>
					</select>
				</div>

				<div className="flex items-start gap-4">
					<label className="text-monadWhite text-sm md:text-base flex items-center gap-1">
						<input
							type="checkbox"
							checked={isBold}
							onChange={(e) => {
								const newValue = e.target.checked;
								setIsBold(newValue);

								// Now update only the selected text layer(s):
								layers.current
									.filter((layer) => layer.type === "text" && layer.isSelected)
									.forEach((layer) => {
										layer.isBold = newValue;
									});

								drawCanvas();
							}}
						/>
						<span style={{ fontWeight: "bold" }}>Bold</span>
					</label>

					<label className="text-monadWhite text-sm md:text-base flex items-center gap-1">
						<input
							type="checkbox"
							checked={isItalic}
							onChange={(e) => {
								const newValue = e.target.checked;
								setIsItalic(e.target.checked);
								layers.current.forEach((layer) => {
									if (layer.type === "text" && layer.isSelected) {
										layer.isItalic = newValue;
									}
								});
								drawCanvas();
							}}
						/>
						<span style={{ fontStyle: "italic" }}>Italic</span>
					</label>
					<label className="text-monadWhite text-sm md:text-base flex items-center gap-1">
						<input
							type="checkbox"
							checked={isStrikethrough}
							onChange={(e) => {
								const newValue = e.target.checked;
								setIsStrikethrough(e.target.checked);
								layers.current.forEach((layer) => {
									if (layer.type === "text" && layer.isSelected) {
										layer.isStrikethrough = newValue;
									}
								});
								drawCanvas();
							}}
						/>
						<span style={{ textDecoration: "line-through" }}>Strike</span>
					</label>
				</div>

				<div className="flex items-start gap-4">
					<label className="text-monadWhite text-sm md:text-base flex items-center gap-1">
						<input
							type="checkbox"
							checked={isUnderlined}
							onChange={(e) => {
								const newValue = e.target.checked;
								setIsUnderlined(e.target.checked);
								layers.current.forEach((layer) => {
									if (layer.type === "text" && layer.isSelected) {
										layer.isUnderlined = newValue;
									}
								});
								drawCanvas();
							}}
						/>
						<span style={{ textDecoration: "underline" }}>Underline</span>
					</label>
					<label className="text-monadWhite text-sm md:text-base flex items-center gap-1">
						<input
							type="checkbox"
							checked={isAllCaps}
							onChange={(e) => {
								const newValue = e.target.checked;
								// Sync the global UI state to reflect the newly toggled value
								setIsAllCaps(newValue);

								// Update only the selected text layer(s)
								layers.current.forEach((layer) => {
									if (layer.type === "text" && layer.isSelected) {
										layer.isAllCaps = newValue;

										// IMPORTANT: force the text to be re-wrapped, because uppercase can widen the text
										layer.recalculateWrap = true;
									}
								});

								// Re-draw after toggling
								drawCanvas();
							}}
						/>
						<span /* optional style */ style={{ textTransform: "uppercase" }}>ALL CAPS</span>
					</label>
				</div>

				<div className="flex items-start gap-2">
					<span className="text-monadWhite text-sm md:text-base">
						Opacity: {textOpacity.toFixed(2)}
					</span>
					<input
						type="range"
						min={0}
						max={1}
						step={0.05}
						value={textOpacity}
						onChange={(e) => {
							const newOpacity = parseFloat(e.target.value);
							setTextOpacity(newOpacity);
							layers.current
								.filter((layer) => layer.type === "text" && layer.isSelected)
								.forEach((layer) => {
									layer.opacity = newOpacity;
								});

							drawCanvas();
						}}
					/>
				</div>

				<div className="flex items-start gap-2">
					<span className="text-monadWhite text-sm md:text-base">Selected Text Color:</span>
					<label className="relative inline-flex items-center cursor-pointer">
						<input
							type="checkbox"
							className="sr-only peer"
							checked={selectedTextIsWhite}
							onChange={(e) => handleColorToggleChange(e.target.checked)}
						/>
						<div
							className="
					w-8 h-4 md:w-11 md:h-6 bg-gray-200 peer-focus:outline-none
					rounded-full peer dark:bg-gray-700
					peer-checked:after:translate-x-full
					peer-checked:after:border-white
					after:content-[''] after:absolute after:top-[2px] after:left-[2px]
					after:bg-white after:border-gray-300 after:border
					after:rounded-full after:h-4 md:after:h-5 after:w-4 md:after:w-5 after:transition-all
					peer-checked:bg-monadAccent
				  "
						/>
						<span className="ml-4 md:ml-2 text-monadWhite text-sm md:text-base">
							{selectedTextIsWhite ? "White" : "Black"}
						</span>
					</label>
				</div>
			</div>
		</div>
	);
}
